'use client';

import React, { useEffect, useState } from 'react';

import { Box, Flex, List, ListIcon, ListItem, Text } from '@chakra-ui/react';
import parse from 'html-react-parser';
import { BsDot } from 'react-icons/bs';

import { CustomText } from '@components/Custom-text';
import { TimelineData } from '@data-types/generic';
import { UserType } from '@enums/index';
import { Mentee, Mentor } from '@modules/auth/types';
import useGetAcademics from '@modules/profile/hooks/useGetAcademics';
import useGetAchievements from '@modules/profile/hooks/useGetAchievements';
import useGetAdditionalExp from '@modules/profile/hooks/useGetAdditionalExp';
import useGetExperience from '@modules/profile/hooks/useGetExperience';
import editorsChoiceIcon from 'src/assets/editor_choice.svg';
import orangeEnterpriseIcon from 'src/assets/orange-enterprise.svg';
import schoolIcon from 'src/assets/pink-school.svg';
import verifiedIcon from 'src/assets/verified.svg';

import MentorPublicProfileCard from '../cards/MentorPublicProfileCard';
import ProfileHighlights from '../cards/ProfileHighlights';
import ProfileTags from '../cards/ProfileTags';
import TimelineSection from '../cards/Timeline';

type PreviewProfileBodyProps = {
    slug: string;
    personalProfileInfo: Mentor;
    isLoading: boolean;
    user: Partial<Mentor | Mentee | null>;
};
const PreviewProfileBody = ({
    slug,
    personalProfileInfo,
    isLoading,
    user,
}: PreviewProfileBodyProps) => {
    const [profExperiences, setProfExperiences] = useState<TimelineData[]>([]);
    const [academics, setAcademics] = useState<TimelineData[]>([]);
    const [profAchievements, setProfAchievements] = useState<string[]>([]);

    // apis
    const { getExperiences, experiences } = useGetExperience({
        userType: UserType.Mentor,
    });

    const { getAcademics, education } = useGetAcademics({
        userType: UserType.Mentor,
    });

    const { getAchievements, achievements } = useGetAchievements({
        userType: UserType.Mentor,
    });

    const { getAdditionalExp, additionalExperience } = useGetAdditionalExp({
        // userType: user?.userType as UserType,
        userType: UserType.Mentor,
    });

    useEffect(() => {
        getExperiences(slug);
        getAcademics(slug);
        getAchievements(slug);
        getAdditionalExp(slug);
    }, [getAcademics, getAchievements, getExperiences, getAdditionalExp, slug]);

    useEffect(() => {
        if (experiences) {
            const updatedExperiences: TimelineData[] = experiences?.experience?.map(item => ({
                timeFrame: `${new Date(item.startDate).getFullYear()} - ${
                    item.endDate ? new Date(item.endDate).getFullYear() : 'Now'
                }`,
                heading: `${item.company} | ${item.location}`,
                subHeading: item.role,
                description: item.description,
            }));
            setProfExperiences(updatedExperiences);
        }
        if (education) {
            const updatedAcademics: TimelineData[] = education?.education?.map(item => ({
                timeFrame: `${new Date(item.startDate).getFullYear()} - ${
                    item.endDate ? new Date(item.endDate).getFullYear() : 'Now'
                }`,
                heading: item.name,
                subHeading: item.university,
            }));
            setAcademics(updatedAcademics);
        }
        if (achievements) {
            const updatedAchievements: string[] = achievements?.achievements?.map(
                item => `${item.title}`
            );
            setProfAchievements(updatedAchievements);
        }
    }, [experiences, education, achievements]);

    return (
        <Flex
            flexDirection="column"
            gap={3}
            minHeight="100vh"
            width={{ base: '100%', sm: '100%', md: '100%' }}
        >
            <MentorPublicProfileCard
                mentorFullProfileInfo={personalProfileInfo!}
                isLoading={isLoading}
                // experiences={profExperiences!}
                // academics={academics!}
                // achievements={profAchievements!}
            />
            <ProfileHighlights mentorFullProfileInfo={personalProfileInfo} isLoading={isLoading} />
            <Box p="24px" borderWidth="1px" borderRadius="md" bgColor="green.50">
                <Flex align="center" gap={2} mb={6}>
                    <Flex
                        width="28px"
                        height="28px"
                        bgColor="green.100"
                        justifyContent="center"
                        alignItems="center"
                        borderRadius="4px"
                    >
                        <img src={verifiedIcon.src} alt="icon" width={20} height={20} />
                    </Flex>
                    <Text
                        color="titleText"
                        size="md"
                        fontWeight={500}
                        fontFamily="Fira Sans"
                        lineHeight={1.2}
                    >
                        Who Should Reach Out to Me?
                    </Text>
                </Flex>
                <Box fontSize="md" color="mainText" px={4}>
                    {additionalExperience?.whoShouldReachOut &&
                        parse(additionalExperience.whoShouldReachOut)}
                </Box>
            </Box>
            <ProfileTags
                mentorFullProfileInfo={personalProfileInfo}
                relevantSkills={additionalExperience?.relevantSkills}
                isLoading={isLoading}
            />
            <TimelineSection
                title="Professional"
                iconSrc={orangeEnterpriseIcon}
                iconBg="orange.100"
                items={profExperiences}
                isLoading={isLoading}
            />
            <TimelineSection
                title="Education"
                iconSrc={schoolIcon}
                iconBg="pink.100"
                items={academics}
                isLoading={isLoading}
            />
            <Box p="24px" borderWidth="1px" borderRadius="md">
                <Flex align="center" gap={2} mb={6}>
                    <Flex
                        width="28px"
                        height="28px"
                        bgColor="yellow.100"
                        justifyContent="center"
                        alignItems="center"
                        borderRadius="4px"
                    >
                        <img src={editorsChoiceIcon.src} alt="icon" width={18} height={18} />
                    </Flex>
                    <Text
                        color="titleText"
                        size="md"
                        fontWeight={500}
                        fontFamily="Fira Sans"
                        lineHeight={1.2}
                    >
                        Achievements
                    </Text>
                </Flex>
                <Box pl={1}>
                    {profAchievements.length > 0 ? (
                        <List spacing={2}>
                            {profAchievements.map((achievement, index) => (
                                <ListItem key={index} display="flex" alignItems="center">
                                    <ListIcon as={BsDot} color="mainText" />
                                    <CustomText color="mainText">{achievement}</CustomText>
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <CustomText color="mainText">No achievements listed yet.</CustomText>
                    )}
                </Box>
            </Box>
        </Flex>
    );
};

export default PreviewProfileBody;
